import { z } from 'zod';

export const BUGCROWD_SUBMISSION_STATES = [
  'new',
  'triaged',
  'informational',
  'not-applicable',
  'not-reproducible',
  'out-of-scope',
  'unresolved',
  'resolved',
] as const;

export const BugcrowdSubmissionStateSchema = z.enum(BUGCROWD_SUBMISSION_STATES);
export type BugcrowdSubmissionState = z.infer<
  typeof BugcrowdSubmissionStateSchema
>;

export const BUGCROWD_SUBMISSION_SOURCES = [
  'api',
  'csv',
  'platform',
  'qualys',
  'external_form',
  'email',
  'jira',
  'customer_source',
] as const;

export const BugcrowdSubmissionSourceSchema = z.enum(
  BUGCROWD_SUBMISSION_SOURCES,
);
export type BugcrowdSubmissionSource = z.infer<
  typeof BugcrowdSubmissionSourceSchema
>;

export const BugcrowdSeveritySchema = z.int().min(1).max(5);
export type BugcrowdSeverity = z.infer<typeof BugcrowdSeveritySchema>;

export const BugcrowdTargetCategorySchema = z.enum([
  'website',
  'api',
  'android',
  'ios',
  'hardware',
  'iot',
  'other',
]);

export const BugcrowdTargetSchema = z.looseObject({
  id: z.string().min(1),
  name: z.string().nullish(),
  category: z.string().nullish(),
});

export type BugcrowdTarget = z.infer<typeof BugcrowdTargetSchema>;

export const BugcrowdProgramRefSchema = z.looseObject({
  id: z.string().min(1),
  code: z.string().nullish(),
  name: z.string().nullish(),
});

export type BugcrowdProgramRef = z.infer<typeof BugcrowdProgramRefSchema>;

export const BugcrowdResearcherRefSchema = z.looseObject({
  id: z.string().min(1),
  name: z.string().nullish(),
});

export type BugcrowdResearcherRef = z.infer<typeof BugcrowdResearcherRefSchema>;

export const BugcrowdCvssVectorRefSchema = z.looseObject({
  id: z.string().min(1),
  score: z.number().nullish(),
  version: z.string().nullish(),
});

export type BugcrowdCvssVectorRef = z.infer<typeof BugcrowdCvssVectorRefSchema>;

export const BugcrowdSubmissionSchema = z.looseObject({
  id: z.string().min(1),
  title: z.string().min(1),
  state: BugcrowdSubmissionStateSchema,
  severity: BugcrowdSeveritySchema.nullish(),
  vrt_id: z.string().nullish(),
  vrt_version: z.string().nullish(),
  description: z.string().nullish(),
  bug_url: z.string().nullish(),
  http_request: z.string().nullish(),
  submitted_at: z.string().nullish(),
  last_activity_feed_item_created_at: z.string().nullish(),
  source: BugcrowdSubmissionSourceSchema.nullish(),
  target: BugcrowdTargetSchema.nullish(),
  program: BugcrowdProgramRefSchema.nullish(),
  researcher: BugcrowdResearcherRefSchema.nullish(),
  cvss_vector: BugcrowdCvssVectorRefSchema.nullish(),
});

export type BugcrowdSubmission = z.infer<typeof BugcrowdSubmissionSchema>;

const BugcrowdRelationshipRefSchema = z.looseObject({
  data: z
    .looseObject({
      id: z.string().min(1),
      type: z.string().nullish(),
    })
    .nullish(),
});

const BugcrowdJsonApiSubmissionAttributesSchema = z.looseObject({
  title: z.string().nullish(),
  state: z.string().nullish(),
  severity: z.number().nullish(),
  vrt_id: z.string().nullish(),
  vrt_version: z.string().nullish(),
  description: z.string().nullish(),
  bug_url: z.string().nullish(),
  http_request: z.string().nullish(),
  submitted_at: z.string().nullish(),
  last_activity_feed_item_created_at: z.string().nullish(),
  source: z.string().nullish(),
});

const BugcrowdJsonApiTargetAttributesSchema = z.looseObject({
  name: z.string().nullish(),
  category: z.string().nullish(),
});

const BugcrowdJsonApiProgramAttributesSchema = z.looseObject({
  name: z.string().nullish(),
  code: z.string().nullish(),
});

const BugcrowdJsonApiIdentityAttributesSchema = z.looseObject({
  name: z.string().nullish(),
});

const BugcrowdJsonApiCvssVectorAttributesSchema = z.looseObject({
  score: z.number().nullish(),
  version: z.string().nullish(),
});

const BugcrowdJsonApiResourceSchema = z.looseObject({
  id: z
    .union([z.string(), z.number()])
    .transform(String)
    .pipe(z.string().min(1)),
  type: z.string().min(1),
  attributes: z.record(z.string(), z.unknown()).optional(),
  relationships: z.record(z.string(), z.unknown()).optional(),
});

type BugcrowdJsonApiResource = z.infer<typeof BugcrowdJsonApiResourceSchema>;

const buildIncludedIndex = (
  included: unknown,
): Map<string, BugcrowdJsonApiResource> => {
  const map = new Map<string, BugcrowdJsonApiResource>();

  if (!Array.isArray(included)) {
    return map;
  }

  for (const entry of included) {
    const parsed = BugcrowdJsonApiResourceSchema.safeParse(entry);

    if (!parsed.success) {
      continue;
    }

    map.set(`${parsed.data.type}:${parsed.data.id}`, parsed.data);
  }

  return map;
};

const parseRelationshipRefId = ({
  relationships,
  key,
}: {
  relationships: Record<string, unknown> | undefined;
  key: string;
}): { id: string; type: string | null } | null => {
  if (!relationships) {
    return null;
  }

  const ref = BugcrowdRelationshipRefSchema.safeParse(relationships[key]);

  if (!ref.success || !ref.data.data?.id) {
    return null;
  }

  return {
    id: ref.data.data.id,
    type: typeof ref.data.data.type === 'string' ? ref.data.data.type : null,
  };
};

const normalizeBugcrowdStateValue = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  return value.replace(/_/g, '-');
};

const parseBugcrowdSubmissionState = (
  value: string | null | undefined,
): BugcrowdSubmissionState => {
  const normalized = normalizeBugcrowdStateValue(value);
  const parsed = BugcrowdSubmissionStateSchema.safeParse(normalized);

  return parsed.success ? parsed.data : 'new';
};

const parseBugcrowdSubmissionSource = (
  value: string | null | undefined,
): BugcrowdSubmissionSource | null => {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }

  const parsed = BugcrowdSubmissionSourceSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

const resolveTarget = ({
  ref,
  included,
}: {
  ref: { id: string; type: string | null } | null;
  included: Map<string, BugcrowdJsonApiResource>;
}): BugcrowdTarget | null => {
  if (!ref) {
    return null;
  }

  const key = `${ref.type ?? 'target'}:${ref.id}`;
  const resource = included.get(key);
  const attributes = resource
    ? BugcrowdJsonApiTargetAttributesSchema.safeParse(resource.attributes ?? {})
    : null;

  return {
    id: ref.id,
    name: attributes?.success ? (attributes.data.name ?? null) : null,
    category: attributes?.success ? (attributes.data.category ?? null) : null,
  };
};

const resolveProgram = ({
  ref,
  included,
}: {
  ref: { id: string; type: string | null } | null;
  included: Map<string, BugcrowdJsonApiResource>;
}): BugcrowdProgramRef | null => {
  if (!ref) {
    return null;
  }

  const key = `${ref.type ?? 'program'}:${ref.id}`;
  const resource = included.get(key);
  const attributes = resource
    ? BugcrowdJsonApiProgramAttributesSchema.safeParse(
        resource.attributes ?? {},
      )
    : null;

  return {
    id: ref.id,
    name: attributes?.success ? (attributes.data.name ?? null) : null,
    code: attributes?.success ? (attributes.data.code ?? null) : null,
  };
};

const resolveResearcher = ({
  ref,
  included,
}: {
  ref: { id: string; type: string | null } | null;
  included: Map<string, BugcrowdJsonApiResource>;
}): BugcrowdResearcherRef | null => {
  if (!ref) {
    return null;
  }

  const key = `${ref.type ?? 'identity'}:${ref.id}`;
  const resource = included.get(key);
  const attributes = resource
    ? BugcrowdJsonApiIdentityAttributesSchema.safeParse(
        resource.attributes ?? {},
      )
    : null;

  return {
    id: ref.id,
    name: attributes?.success ? (attributes.data.name ?? null) : null,
  };
};

const resolveCvssVector = ({
  ref,
  included,
}: {
  ref: { id: string; type: string | null } | null;
  included: Map<string, BugcrowdJsonApiResource>;
}): BugcrowdCvssVectorRef | null => {
  if (!ref) {
    return null;
  }

  const key = `${ref.type ?? 'cvss_vector'}:${ref.id}`;
  const resource = included.get(key);
  const attributes = resource
    ? BugcrowdJsonApiCvssVectorAttributesSchema.safeParse(
        resource.attributes ?? {},
      )
    : null;

  return {
    id: ref.id,
    score: attributes?.success ? (attributes.data.score ?? null) : null,
    version: attributes?.success ? (attributes.data.version ?? null) : null,
  };
};

export const parseBugcrowdJsonApiSubmission = ({
  item,
  included,
}: {
  item: unknown;
  included?: unknown;
}): BugcrowdSubmission | null => {
  const itemResult = BugcrowdJsonApiResourceSchema.safeParse(item);

  if (!itemResult.success) {
    return null;
  }

  const attributesResult = BugcrowdJsonApiSubmissionAttributesSchema.safeParse(
    itemResult.data.attributes ?? {},
  );
  const attributes = attributesResult.success ? attributesResult.data : {};

  const includedIndex = buildIncludedIndex(included);
  const targetRef = parseRelationshipRefId({
    relationships: itemResult.data.relationships,
    key: 'target',
  });
  const programRef = parseRelationshipRefId({
    relationships: itemResult.data.relationships,
    key: 'program',
  });
  const researcherRef = parseRelationshipRefId({
    relationships: itemResult.data.relationships,
    key: 'researcher',
  });
  const cvssVectorRef = parseRelationshipRefId({
    relationships: itemResult.data.relationships,
    key: 'cvss_vector',
  });

  const submissionInput: z.input<typeof BugcrowdSubmissionSchema> = {
    id: itemResult.data.id,
    title: attributes.title ?? `Bugcrowd submission ${itemResult.data.id}`,
    state: parseBugcrowdSubmissionState(attributes.state),
    severity: attributes.severity,
    vrt_id: attributes.vrt_id,
    vrt_version: attributes.vrt_version,
    description: attributes.description,
    bug_url: attributes.bug_url,
    http_request: attributes.http_request,
    submitted_at: attributes.submitted_at,
    last_activity_feed_item_created_at:
      attributes.last_activity_feed_item_created_at,
    source: parseBugcrowdSubmissionSource(attributes.source),
    target: resolveTarget({ ref: targetRef, included: includedIndex }),
    program: resolveProgram({ ref: programRef, included: includedIndex }),
    researcher: resolveResearcher({
      ref: researcherRef,
      included: includedIndex,
    }),
    cvss_vector: resolveCvssVector({
      ref: cvssVectorRef,
      included: includedIndex,
    }),
  };

  const submissionResult = BugcrowdSubmissionSchema.safeParse(submissionInput);

  return submissionResult.success ? submissionResult.data : null;
};

export const parseBugcrowdJsonApiProgram = ({
  item,
}: {
  item: unknown;
}): BugcrowdProgramRef | null => {
  const itemResult = BugcrowdJsonApiResourceSchema.safeParse(item);

  if (!itemResult.success || itemResult.data.type !== 'program') {
    return null;
  }

  const attributesResult = BugcrowdJsonApiProgramAttributesSchema.safeParse(
    itemResult.data.attributes ?? {},
  );
  const attributes = attributesResult.success ? attributesResult.data : {};

  const programResult = BugcrowdProgramRefSchema.safeParse({
    id: itemResult.data.id,
    code: attributes.code ?? null,
    name: attributes.name ?? null,
  });

  return programResult.success ? programResult.data : null;
};

export const BugcrowdProgramsListResponseSchema = z.looseObject({
  data: z.array(z.unknown()).optional(),
});

export type BugcrowdProgramsListResponse = z.infer<
  typeof BugcrowdProgramsListResponseSchema
>;

export const BugcrowdSubmissionsListResponseSchema = z.looseObject({
  data: z.array(z.unknown()).optional(),
  included: z.array(z.unknown()).optional(),
  meta: z
    .looseObject({
      total_hits: z.number().nullish(),
    })
    .optional(),
  links: z
    .looseObject({
      next: z.string().nullish(),
    })
    .optional(),
});

export type BugcrowdSubmissionsListResponse = z.infer<
  typeof BugcrowdSubmissionsListResponseSchema
>;

export const BugcrowdSubmissionDetailResponseSchema = z.looseObject({
  data: z.unknown().optional(),
  included: z.array(z.unknown()).optional(),
});

export type BugcrowdSubmissionDetailResponse = z.infer<
  typeof BugcrowdSubmissionDetailResponseSchema
>;
